import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from "vue";
import { useEUAccountsStore } from "@/store_v3/EUAccountsStore";
import { storeToRefs } from "pinia";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgCheck from "@/assets/icons/check.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import { IEUAccountServices, IEUAccountStatsServiceItem } from "@/api/types/revenue";
import { type ITableHeader } from "@/utils";
import { prepareItems, type RouteCellData } from "@/utils/CellsFactory";
import RouteCell from "@/components-UI/table/cells/RouteCell.vue";
import router from "@/router";
import { translateGoalStatus, translateStatTaskServiceStatus } from "@/utils/translate";
import type { IStatus } from "@/types";
import BaseStatusV2 from "@/components-UI/BaseStatusV2.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentServicesTabStats',
  props: {
    statIndex: null
  },
  setup(__props: any) {

const props = __props as {
    statIndex?: number,
}



const EUAccountsStore = useEUAccountsStore();
const {
    isEdit,
    form,
} = storeToRefs(EUAccountsStore);
const {
    formatNumber,
} = EUAccountsStore;

const selectedService = ref<Array<string | number>>([]);

const tableGroupedData = computed(() => {
    const index = props.statIndex ?? 0;
    const statsServices = form.value.stats?.[index]?.services || [] as IEUAccountStatsServiceItem[];

    const grouped = statsServices.reduce((acc, service) => {
        const offerId = service.offer_id;

        const offerName = service.offer_name ?? services.value.find((s: IEUAccountServices, idx: number) => {
            return service.originalIndex === idx ||
                (s.offer_id === service.offer_id &&
                 (s.goal_id === service.goal_id || s.goal_service_id === service.goal_id));
        })?.offer_name ?? '';

        if (!acc[offerId]) {
            acc[offerId] = {
                offer_id: offerId,
                offer_name: offerName,
                services: [],
            };
        }

        acc[offerId].services.push(service);
        return acc;
    }, {} as Record<number, { offer_id: number; offer_name: string; services: IEUAccountStatsServiceItem[] }>);

    return Object.values(grouped);
});

const services = computed(() => form.value.services || []);

const servicesWithId = computed(() => {
    return services.value.map((service: IEUAccountServices, index: number) => ({
        ...service,
        uniqueId: `${service.offer_id}_${service.goal_id}_${index}`,
    }));
});

const addedServicesIndices = computed(() => {
    const indices = new Set<number>();
    (form.value.stats ?? []).forEach((stat) => {
        (stat?.services || []).forEach((service: IEUAccountStatsServiceItem) => {
            if (service.originalIndex !== undefined) {
                indices.add(service.originalIndex);
            }
        });
    });
    return indices;
});

const addedServicesKeyCounts = computed(() => {
    const counts = new Map<string, number>();
    (form.value.stats ?? []).forEach((stat) => {
        const blockServices = (stat?.services || []) as IEUAccountStatsServiceItem[];
        blockServices.forEach((s) => {
            if (s.originalIndex === undefined) {
                const key = `${s.offer_id}_${s.goal_id}_${s.goal_service_id ?? s.goal_id}`;
                counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        });
    });
    return counts;
});

const availableServicesForSelect = computed(() => {
    const keyCounts = new Map<string, number>(addedServicesKeyCounts.value);

    return servicesWithId.value.filter((service: IEUAccountServices & { uniqueId?: string }, index: number) => {
        if (addedServicesIndices.value.has(index)) return false;
        const key = `${service.offer_id}_${service.goal_id}_${service.goal_service_id ?? service.goal_id}`;
        const count = keyCounts.get(key) ?? 0;
        if (count > 0) {
            keyCounts.set(key, count - 1);
            return false;
        }
        return true;
    });
});

const isSingle = computed(() => router.currentRoute.params.id !== 'create');

const formatServiceTemplate = (item: IEUAccountServices & { uniqueId?: string }): string => {
    const offerName = item.offer_name || '';
    const offerId = item.offer_id || '';
    const goalName = item.goal_name || '';
    const goalId = item.goal_id || '';
    const rate = item.original_amount || 0;
    const isPercentGoal = item.goal_revenue_type === 'cpa_percentage' || item.goal_revenue_type === 'percent';
    const currency = isPercentGoal ? form.value.currency : (item.goal_currency || '');
    
    return `${offerName} (${offerId}) - ${goalName} (${goalId}) : ${rate} ${currency}`;
};

const handleSelectService = () => {
    if (selectedService.value && selectedService.value.length > 0) {
        const index = props.statIndex ?? 0;

        if (!form.value.stats) {
            form.value.stats = [];
        }

        if (!form.value.stats[index]) {
            form.value.stats[index] = {
                id: 0,
                services: [],
                file: null,
                comment: '',
            };
        }

        const selectedServices = availableServicesForSelect.value
            .filter((service) => selectedService.value.includes(service.uniqueId))
            .map((service) => {
                const { uniqueId } = service;
                const parts = uniqueId.split('_');
                const parsed = parseInt(parts[parts.length - 1], 10);
                const originalIndex = Number.isFinite(parsed) ? parsed : 0;

                const startRate = service.goal_revenue_flat
                    ? Number(service.goal_revenue_flat)
                    : (service.original_amount ? Number(service.original_amount) : 0);

                const finalRate = service.original_amount
                    ? Number(service.original_amount)
                    : startRate;
                
                const serviceItem: IEUAccountStatsServiceItem = {
                    offer_id: service.offer_id,
                    goal_id: service.goal_id,
                    goal_service_id: service.goal_service_id ?? service.goal_id,
                    start_rate: startRate,
                    final_rate: finalRate,
                    quantity: Number(service.quantity) || 0,
                    originalIndex,
                    offer_name: service.offer_name,
                    goal_name: service.goal_name,
                    goal_currency: service.goal_currency,
                };
                
                return serviceItem;
            });

        const currentData = form.value.stats[index].services as IEUAccountStatsServiceItem[];
        currentData.push(...selectedServices);
        form.value.stats[index].services = currentData;

        selectedService.value = [];
    }
};

const isRouteColumn = (field: string): boolean => {
    return ['offer_name', 'goal_service_id', 'goal_name'].includes(field);
};

const getOfferRouteData = (group: { offer_id: number; offer_name?: string }): RouteCellData => {
    const originalService = services.value.find((s: IEUAccountServices) => s.offer_id === group.offer_id);
    const offerName = group.offer_name || originalService?.offer_name || '';
    
    return {
        name: 'offer',
        valueKey: 'offer_id',
        text: offerName ? `${group.offer_id} - ${offerName}` : String(group.offer_id),
    };
};

const getServiceRouteData = (service: IEUAccountStatsServiceItem, field: string): RouteCellData => {
    const originalService = services.value.find((s: IEUAccountServices, idx: number) => {
        return service.originalIndex === idx ||
            (s.offer_id === service.offer_id &&
             (s.goal_id === service.goal_id || s.goal_service_id === service.goal_id));
    });
    
    switch (field) {
    case 'offer_name':
        return {
            name: 'offer',
            valueKey: 'offer_id',
            text: 'offer_name',
        };
    case 'goal_service_id': {
        const goalServiceId = originalService?.goal_service_id ?? service.goal_service_id ?? service.goal_id ?? originalService?.goal_id;
        return {
            name: 'offer',
            valueKey: 'offer_id',
            isChip: true,
            text: String(goalServiceId ?? ''),
            isTextCenter: true,
        };
    }
    case 'goal_name':
        return {
            name: 'offer',
            valueKey: 'offer_id',
            text: service.goal_name ?? originalService?.goal_name ?? '',
        };
    default:
        return {
            name: 'offer',
            valueKey: 'offer_id',
        };
    }
};

const getOriginalService = (service: IEUAccountStatsServiceItem): IEUAccountServices | undefined =>
    services.value.find((s: IEUAccountServices, idx: number) =>
        service.originalIndex === idx ||
        (s.offer_id === service.offer_id &&
         (s.goal_id === service.goal_id || s.goal_service_id === service.goal_id)),
    );

const getServiceStatus = (service: IEUAccountStatsServiceItem): IStatus => {
    if (!isSingle.value) return { text: '', color: '' };
    const status = service.status;
    if (!status) return { text: '', color: '' };
    return translateStatTaskServiceStatus(status);
};

const getServiceValue = (service: IEUAccountStatsServiceItem, field: string): string | number => {
    const originalService = getOriginalService(service);
    
    switch (field) {
    case 'current_rate':
        if (service.start_rate != null) return formatNumber(service.start_rate);
        if (originalService) {
            if (originalService.goal_revenue_type === 'cpa_flat' && originalService.goal_revenue_flat) {
                return formatNumber(originalService.goal_revenue_flat);
            }
            if ((originalService.goal_revenue_type === 'cpa_percentage' || originalService.goal_revenue_type === 'percent') && originalService.goal_revenue_percent) {
                return `${formatNumber(originalService.goal_revenue_percent, [2, 0]).replace(/,0+$/, '').replace(/,$/, '')}%`;
            }
        }
        return '';
    case 'original_amount':
        return formatNumber(service.final_rate);
    case 'goal_currency': {
        const isPercentGoal = originalService?.goal_revenue_type === 'cpa_percentage' || originalService?.goal_revenue_type === 'percent';
        return isPercentGoal ? form.value.currency : (service.goal_currency ?? originalService?.goal_currency ?? '');
    }
    case 'quantity':
        return service.quantity ?? '';
    case 'goal_service_id':
        return originalService?.goal_service_id ?? service.goal_service_id ?? service.goal_id ?? '';
    case 'goal_status':
        return originalService?.goal_status != null ? translateGoalStatus(originalService.goal_status).text : '';
    default:
        const rowValue = service[field as keyof IEUAccountStatsServiceItem];
        if (rowValue !== undefined && rowValue !== '') return rowValue as string | number;
        if (originalService) {
            return originalService[field as keyof IEUAccountServices] as string | number ?? '';
        }
        return '';
    }
};

const removeServiceFromTable = (groupIndex: number, serviceIndex: number): void => {
    const index = props.statIndex ?? 0;
    if (!form.value.stats || !form.value.stats[index]) {
        return;
    }
    
    const services = form.value.stats[index].services as IEUAccountStatsServiceItem[];
    const grouped = tableGroupedData.value;
    const group = grouped[groupIndex];
    
    if (group && group.services && group.services[serviceIndex]) {
        const serviceToRemove = group.services[serviceIndex];
        const serviceIndexInFlat = services.findIndex(
            (s: IEUAccountStatsServiceItem) =>
                s.offer_id === serviceToRemove.offer_id &&
                s.goal_id === serviceToRemove.goal_id &&
                s.start_rate === serviceToRemove.start_rate &&
                s.final_rate === serviceToRemove.final_rate &&
                s.quantity === serviceToRemove.quantity,
        );
        
        if (serviceIndexInFlat > -1) {
            services.splice(serviceIndexInFlat, 1);
            form.value.stats[index].services = services;
        }
    }
};

const handleDeleteAll = () => {
    const index = props.statIndex ?? 0;
    if (!form.value.stats || !form.value.stats[index]) {
        return;
    }
    form.value.stats[index].services = [] as IEUAccountStatsServiceItem[];
};

const tableHeaders: ITableHeader[] = [
    { text: 'Оффер', value: 'offer_name', width: 'auto' },
    { text: 'ID Цели', value: 'goal_service_id', width: '77px', align: 'center' },
    { text: 'Цель', value: 'goal_name', width: 'auto' },
    { text: 'Текущая ставка', value: 'current_rate', width: 'auto', align: 'right' },
    { text: 'Ставка', value: 'original_amount', width: 'auto', align: 'right' },
    { text: 'Валюта', value: 'goal_currency', width: 'auto', align: 'center' },
    { text: 'Кол-во', value: 'quantity', width: 'auto', align: 'center' },
    { text: 'Статус', value: 'goal_status', width: '102px', align: 'end' },
    { text: '', value: 'actions', width: '48px', align: 'center' },
];

const showActionsColumn = computed(() => !isSingle.value || isEdit.value);

const hiddenTableColumns = computed(() => [
    ...(!isSingle.value || isEdit.value ? ['goal_status'] : []),
    ...(isSingle.value && !isEdit.value ? ['actions'] : []),
]);

const visibleBodyHeaders = computed(() =>
    tableHeaders
        .slice(1)
        .filter((h) => (h.value !== 'actions' || showActionsColumn.value) && (h.value !== 'goal_status' || (isSingle.value && !isEdit.value))),
);

const getTableFormatItems = computed(() => prepareItems([], {}, tableHeaders.map((h) => h.value)));

return { __sfc: true,props, EUAccountsStore, isEdit, form, formatNumber, selectedService, tableGroupedData, services, servicesWithId, addedServicesIndices, addedServicesKeyCounts, availableServicesForSelect, isSingle, formatServiceTemplate, handleSelectService, isRouteColumn, getOfferRouteData, getServiceRouteData, getOriginalService, getServiceStatus, getServiceValue, removeServiceFromTable, handleDeleteAll, tableHeaders, showActionsColumn, hiddenTableColumns, visibleBodyHeaders, getTableFormatItems, BaseTable, BaseSelect, BaseButton, SvgCheck, SvgTrash, RouteCell, BaseStatusV2 }
}

})